<template>
	<div class="p-grid">
		<div class="p-col-12">
			<div class="card">

        <Toolbar class="p-mb-4">
          <template #start>
            <div style="font-size: 1.4em; color: inherit" class="p-mr-3">{{ $t('Service categories') }}</div>
            <AddNewButton v-show="canModerate" :label="$t('Add')" @click="addNewItem"/>
          </template>
          <template #end>
          </template>
        </Toolbar>

        <Spinner v-if="isLoading"/>
        <DesktopDataTable v-else
                          :showHeader="false"
                          :dataTableIsLoading="dataTableIsLoading"
                          :itemsArrayIsEmpty="itemsArrayIsEmpty"
                          :sort="sort"
                          :showSearchInput="false"
                          :actionButtonsCount="actionButtonsCount"
                          @sort-items="sortItems"
                          :headers="[
                            {name: 'name', title: $t('Name'),sortable: true},
                            {name: 'active_items_count', title: $t('Count'), width: '20%', sortable: false},
                            {name: 'created_at', title: $t('Created'), width: '20%', sortable: true },
                          ]">
          <template v-slot:body>
            <tr v-for="(item, index) of items"
                :key="index"
                :class="{'updated-item': isItemUpdated(item.id)}">
              <td :class="{'bold-text':item.isUncategorized}">
                <span v-if="item.isUncategorized">{{ $t('Uncategorized') }}</span>
                <template v-else>
                  <span v-if="item.name">{{ item.name }}</span>
                  <i v-else class="ti-minus"/>
                </template>
              </td>
              <td :class="{'bold-text':item.isUncategorized}">
                <span v-if="item.servicesCount !== null && item.servicesCount !== undefined">{{ item.servicesCount }}</span>
                <i v-else class="ti-minus"/>
              </td>
              <td class="table-date-line">
                <span v-if="item.created_at">{{ formatBy(item.created_at, item.createdBy) }}</span>
                <i v-else class="ti-minus"/>
              </td>
              <td v-show="actionButtonsCount">
                <div class="actions" v-if="!item.isUncategorized">
                  <EditButton v-show="canModerate"
                              :disabled="dataTableIsLoading" @click="editItem(item)" />
                  <DeleteButton v-show="canModerate"
                                @click="confirmItemDelete(item)"
                                :disabled="dataTableIsLoading"/>
                </div>
              </td>
            </tr>
          </template>
        </DesktopDataTable>

        <OwnPaginator v-show="!itemsArrayIsEmpty"
                      :rows="pagination.perPage"
                      :currentPage="pagination.currentPage - 1"
                      :totalPages="pagination.pageCount"
                      :totalRecords="pagination.totalCount"
                      :rowsPerPageOptions="[5,10,25,50,100,200]"
                      :pageLinkSize="11"/>

        <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>

        <ServiceCategoryModal :visible="itemModal"
                                :item="item"
                                @update-items="updateItems"
                                @close="closeItemModal"/>

        <ConfirmDeleteModal :visible="confirmItemDeleteModal"
                            :modalDataIsLoading="modalDataIsLoading"
                            @close="closeConfirmItemDeleteModal"
                            @delete-item="deleteItem"
                            :data="itemToDeleteData"/>
			</div>
		</div>
	</div>
</template>

<script>
import httpClient from '@/services/http.services'
import formatMixins from "@/mixins/formatMixins";
import datatableMixins from "@/mixins/datatableMixins";
import settings from "@/settings";
import sortingMixins from "@/mixins/sortingMixins";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import ServiceCategoryModal from "@/pages/data_directory/components/ServiceCategoryModal";

export default {
  mixins: [ formatMixins, datatableMixins, showErrorsMixins, sortingMixins ],
  components: { ServiceCategoryModal },
	data() {
		return {
      fieldsExpand: 'createdBy,servicesCount',
      apiLink: 'service/get-categories',
      deleteItemUrl: 'service/delete-category',
      routePath: '/service-categories',
      sort: {
        column: 'name',
        direction: '',
      },
		}
	},
  mounted() {
    this.scrollToTop()
    if (settings.autoLogoutTimeout && this.$store.state.logoutTimestampIsOverdue) return false
    this.checkRouteQuery()
    this.getPageData()
	},
  methods: {
    getPageData() {
      this.getItems()
    },
    async getItems() {
      if (!this.userCanGetData || this.fetchInFlight) return
      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true
      const ctrl = this.setController?.('serviceCategories')
      try {
        // const { status, data, headers } = await httpClient(`${this.apiLink}?page=${this.pagination.currentPage}&per-page=${this.pagination.perPage}&sort=${this.sort.direction + this.sort.column}&search=${this.searchData}&expand=${this.fieldsExpand}`)
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          params: {
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.processedSearchValue ?? null,
            expand: this.fieldsExpand,
          },
          signal: ctrl?.signal
        })
        if (status === 200 && data) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']
          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount  = 1
          }

          // const items = data.categories && Array.isArray(data.categories) ? data.categories : []
          //
          // this.items = items
          //
          // const uncategorized_count = data.uncategorized_count ? +data.uncategorized_count : 0
          // if (uncategorized_count) {
          //   this.items.push({
          //     isUncategorized: true,
          //     servicesCount: uncategorized_count,
          //   })
          // }
          //
          // if (!items.length) {
          //   this.pagination.pageCount = 1
          //   this.pagination.currentPage = 1
          // }

          const categories = Array.isArray(data.categories) ? data.categories : []

          this.items = [...categories]

          const uncategorizedCount = Number(data.uncategorized_count || 0)

          if (uncategorizedCount) {
            this.items.push({
              isUncategorized: true,
              servicesCount: uncategorizedCount,
            })
          }

          if (!categories.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch(err) {
        this.showError(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.dataTableIsLoading = false
        this.isLoading = false
        if (this._abortors?.serviceCategories === ctrl) {
          this._abortors.serviceCategories = null
        }
      }
    },
	},
  computed: {
    canModerate() {
      return this.$store.getters.isAccountantOrHigher
    },
    actionButtonsCount() {
      return this.canModerate ? 2 : 0
    },
    itemToDeleteData() {
      if (!this.itemToDelete?.name) return null
      return this.itemToDelete.name
    },
  }
}
</script>

<style scoped lang="scss">
td {
  height: 44px
}
</style>